/**
 * PushNotificationBanner — prompts the user to enable device push.
 *
 * Hard rule we follow: the BACKEND is the source of truth for "is this user
 * actually subscribed on this device?". localStorage flags lie (stale after a
 * subscription is cleaned up server-side; don't survive device reinstalls;
 * lead to the 4-of-43-users-subscribed gap we had before).
 *
 * Behaviour:
 *   1. If the browser doesn't support push OR permission was already denied
 *      by the user — show nothing (there is no path forward without OS
 *      settings change).
 *   2. If `Notification.permission === 'granted'` AND the backend has NO
 *      active subscription for this device → silently re-subscribe. No
 *      banner needed: the user has already said yes, we just rebuild the
 *      subscription. This recovers users whose endpoint died.
 *   3. Otherwise (permission = 'default' and no active sub) → show the
 *      banner until they click Enable or dismiss. Dismiss is SESSION-only,
 *      so the next login re-prompts.
 *
 * Mounted globally from Layout.js so it appears on every authenticated page
 * — not just the dashboard.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Bell, X } from 'lucide-react';
import { Button } from './ui/button';
import { useAuth } from '../contexts/AuthContext';
import { apiRequest } from '../lib/utils';
import {
  isPushSupported,
  getNotificationPermission,
  subscribeToPush,
} from '../utils/pushNotifications';

export default function PushNotificationBanner() {
  const { token, user } = useAuth();
  const [showBanner, setShowBanner] = useState(false);
  const [loading, setLoading] = useState(false);

  const check = useCallback(async () => {
    if (!user || !token) return;
    if (!isPushSupported()) return;
    if (sessionStorage.getItem('push_banner_dismissed')) return;

    const perm = getNotificationPermission();
    if (perm === 'denied') return; // no path forward without OS settings change

    // Source of truth: backend knows if this device has an active subscription.
    let subscribed = false;
    try {
      const status = await apiRequest('/push/status');
      subscribed = !!status?.subscribed;
    } catch (_) { /* if the endpoint is briefly down, err on showing the banner */ }

    if (perm === 'granted' && !subscribed) {
      // User already said yes — silently rebuild the subscription. No gesture
      // required because permission is already granted.
      try {
        await subscribeToPush(token);
      } catch (e) {
        // If the silent resubscribe fails (rare — only happens on iOS when
        // the PWA hasn't been opened in a while) we fall back to the banner
        // so the next user gesture re-enables push.
        console.warn('[push] silent resubscribe failed:', e);
        setShowBanner(true);
      }
      return;
    }

    if (perm === 'default' && !subscribed) {
      setShowBanner(true);
    }
  }, [user, token]);

  useEffect(() => { check(); }, [check]);

  const handleEnable = async () => {
    setLoading(true);
    try {
      await subscribeToPush(token);
      setShowBanner(false);
    } catch (error) {
      console.error('Failed to enable push:', error);
      // If denied, there's nothing more we can do from JS.
      if (error.message && error.message.toLowerCase().includes('denied')) {
        setShowBanner(false);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = () => {
    setShowBanner(false);
    sessionStorage.setItem('push_banner_dismissed', 'true');
  };

  if (!showBanner) return null;

  return (
    <div
      className="fixed left-3 right-3 lg:left-auto lg:right-6 lg:w-[380px] bottom-[88px] lg:bottom-6 z-40 bg-gradient-to-r from-[#DC2626] to-[#B91C1C] text-white p-4 rounded-xl shadow-2xl"
      data-testid="push-notification-banner"
    >
      <div className="flex items-start gap-3">
        <div className="bg-white/20 p-2 rounded-full shrink-0">
          <Bell className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-base">Turn on notifications</h3>
          <p className="text-white/90 text-xs mt-1">
            Get alerts for tasks, messages, approvals and more — even when the app is closed.
          </p>
          <div className="flex gap-2 mt-3">
            <Button
              onClick={handleEnable}
              disabled={loading}
              className="bg-white text-[#DC2626] hover:bg-white/90 h-8 px-3"
              size="sm"
              data-testid="push-banner-enable"
            >
              {loading ? 'Enabling…' : 'Enable'}
            </Button>
            <Button
              onClick={handleDismiss}
              variant="ghost"
              className="text-white hover:bg-white/20 h-8 px-3"
              size="sm"
              data-testid="push-banner-dismiss"
            >
              Not now
            </Button>
          </div>
        </div>
        <button
          onClick={handleDismiss}
          className="text-white/70 hover:text-white shrink-0"
          aria-label="Dismiss"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
